import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, computed, effect, inject, signal, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { Subject, of } from 'rxjs';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SearchResult, SearchService } from '../../../core/services/search.service';
import { resolveSearchResultRoute } from '../../../core/utils/search-result-route.util';
import { getSearchEntityMeta } from '../../../core/utils/search-entity-meta.util';
import { SEARCH_GOTO_SECTIONS, SearchGotoSection } from '../../../core/utils/search-goto-sections.util';
import { highlightMatch, HighlightedText } from '../../../core/utils/highlight-match.util';

const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

interface PaletteItem {
    kind: 'goto' | 'result';
    key: string;
    icon: string;
    title: string;
    subtitle: string;
    flatIndex: number;
    goto?: SearchGotoSection;
    result?: SearchResult;
}

interface PaletteGroup {
    headerKey: string;
    items: PaletteItem[];
}

@Component({
    selector: 'topbar-search-palette',
    standalone: true,
    imports: [TranslatePipe, DialogModule],
    templateUrl: './topbar-search-palette.component.html',
    styleUrl: './topbar-search-palette.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TopbarSearchPaletteComponent {
    private readonly router = inject(Router);
    private readonly translate = inject(TranslateService);
    private readonly searchService = inject(SearchService);
    private readonly destroyRef = inject(DestroyRef);

    readonly queryInput = viewChild<ElementRef<HTMLInputElement>>('queryInput');

    readonly visible = signal(false);
    readonly query = signal('');
    readonly loading = signal(false);
    readonly error = signal(false);
    readonly activeIndex = signal(0);
    private readonly results = signal<SearchResult[]>([]);

    private readonly search$ = new Subject<string>();

    private readonly gotoMatches = computed<SearchGotoSection[]>(() => {
        const q = this.query().trim().toLocaleLowerCase();
        if (!q) {
            return [];
        }
        return SEARCH_GOTO_SECTIONS.filter((section) =>
            this.translate.instant(section.labelKey).toLocaleLowerCase().includes(q)
        );
    });

    readonly displayGroups = computed<PaletteGroup[]>(() => {
        const q = this.query().trim();
        let nextIndex = 0;
        const nextFlatIndex = () => nextIndex++;

        if (!q) {
            return [{
                headerKey: 'app.topbar.search.goto',
                items: SEARCH_GOTO_SECTIONS.map((section) => this.toGotoItem(section, nextFlatIndex()))
            }];
        }

        const groups: PaletteGroup[] = [];

        const goto = this.gotoMatches();
        if (goto.length) {
            groups.push({
                headerKey: 'app.topbar.search.section_navigation',
                items: goto.map((section) => this.toGotoItem(section, nextFlatIndex()))
            });
        }

        // Se agrupa por labelKey (no por `entity` en crudo): subtipos como
        // position/position_militar o group/group_militar comparten la
        // misma categoría visual y no deben abrir dos cabeceras iguales.
        const byLabelKey = new Map<string, { meta: ReturnType<typeof getSearchEntityMeta>; items: SearchResult[] }>();
        for (const result of this.results()) {
            const meta = getSearchEntityMeta(result.entity);
            const bucket = byLabelKey.get(meta.labelKey) ?? { meta, items: [] };
            bucket.items.push(result);
            byLabelKey.set(meta.labelKey, bucket);
        }

        const entityGroups = [...byLabelKey.values()].sort((a, b) => a.meta.order - b.meta.order);

        for (const group of entityGroups) {
            groups.push({
                headerKey: group.meta.labelKey,
                items: group.items.map((result) => this.toResultItem(result, nextFlatIndex()))
            });
        }

        return groups;
    });

    readonly flatItems = computed<PaletteItem[]>(() => this.displayGroups().flatMap((g) => g.items));
    readonly hasQuery = computed(() => this.query().trim().length > 0);
    readonly showNoResults = computed(() =>
        this.hasQuery() && !this.loading() && !this.error() && this.flatItems().length === 0
    );

    constructor() {
        this.search$.pipe(
            debounceTime(SEARCH_DEBOUNCE_MS),
            switchMap((term) => {
                if (term.trim().length < MIN_QUERY_LENGTH) {
                    this.loading.set(false);
                    return of<SearchResult[]>([]);
                }
                this.loading.set(true);
                this.error.set(false);
                return this.searchService.search(term.trim()).pipe(
                    catchError(() => {
                        this.error.set(true);
                        return of<SearchResult[]>([]);
                    })
                );
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe((results) => {
            this.results.set(results);
            this.loading.set(false);
            this.activeIndex.set(0);
        });

        effect(() => {
            if (this.visible()) {
                setTimeout(() => this.queryInput()?.nativeElement.focus());
            } else {
                this.query.set('');
                this.results.set([]);
                this.error.set(false);
                this.loading.set(false);
                this.activeIndex.set(0);
            }
        });
    }

    private toGotoItem(section: SearchGotoSection, flatIndex: number): PaletteItem {
        return {
            kind: 'goto',
            key: `goto-${section.id}`,
            icon: section.icon,
            title: this.translate.instant(section.labelKey),
            subtitle: this.translate.instant(section.subtitleKey),
            flatIndex,
            goto: section
        };
    }

    private toResultItem(result: SearchResult, flatIndex: number): PaletteItem {
        const meta = getSearchEntityMeta(result.entity);
        return {
            kind: 'result',
            key: `result-${result.entity}-${result.id}`,
            icon: meta.icon,
            title: result.display || result.term || '',
            subtitle: result.related_term ?? '',
            flatIndex,
            result
        };
    }

    highlight(text: string): HighlightedText {
        return highlightMatch(text, this.query().trim());
    }

    show(): void {
        this.visible.set(true);
    }

    close(): void {
        this.visible.set(false);
    }

    onQueryChange(value: string): void {
        this.query.set(value);
        this.activeIndex.set(0);
        this.search$.next(value);
    }

    isActive(index: number): boolean {
        return this.activeIndex() === index;
    }

    onArrowDown(): void {
        const len = this.flatItems().length;
        if (!len) {
            return;
        }
        this.activeIndex.update((i) => (i + 1) % len);
    }

    onArrowUp(): void {
        const len = this.flatItems().length;
        if (!len) {
            return;
        }
        this.activeIndex.update((i) => (i - 1 + len) % len);
    }

    onEnter(): void {
        const item = this.flatItems()[this.activeIndex()];
        if (item) {
            this.activate(item);
        }
    }

    activate(item: PaletteItem): void {
        if (item.kind === 'goto' && item.goto) {
            this.router.navigate([item.goto.route]);
        } else if (item.kind === 'result' && item.result) {
            const resolved = resolveSearchResultRoute(item.result);
            if (resolved) {
                this.router.navigate(resolved.commands, resolved.extras);
            }
        }
        this.close();
    }
}
